import { BehaviorSubject, Observable, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { BillingProvider } from '../../containers/payment/models/payment';
import { AppConfig } from '../../core/app.config';
import { BACKGROUND_NETWORK_REQ_OPTIONS } from '../../core/constants/constants';
import { ON_CALL_SUPPORT_PERSON_IMAGE_SHUFFLE_INTERVAL, SUPPORT_PERSON_IMAGES } from '../constants/support';
import { RxRequestService } from '../../core/services/rx-request.service';
import { User } from '../../core/interfaces/user';

declare const __DEPLOYMENT_ENV__: string;

export interface SupportServicePlanDetails {
  billing_provider_type: BillingProvider;
  monthly_spend: number;
  plan: string;
}

export abstract class SupportService {
  supportLink = 'mailto:support-escalations@hevodata.com';

  unreadCount$: Observable<number>;
  user: User;
  planDetails: SupportServicePlanDetails;

  isOnCallSupportAvailable$ = new BehaviorSubject(true);

  /** Whether the Cerebra AI chatbot is enabled for the current user. Resolved once
   *  on login (see {@link setCurrentUser}) so the appbar can decide synchronously
   *  whether clicking the support CTA opens the chat widget or the legacy launcher. */
  chatbotEnabled$ = new BehaviorSubject(false);

  protected _liveChatIsOpen$ = new BehaviorSubject(false);

  onCallSupportPersonImage$ = timer(0, ON_CALL_SUPPORT_PERSON_IMAGE_SHUFFLE_INTERVAL).pipe(
    map(currentIndex => SUPPORT_PERSON_IMAGES[currentIndex % SUPPORT_PERSON_IMAGES.length])
  );

  constructor(protected _appConfig: AppConfig, protected _rxRequestService: RxRequestService) {
  }

  setCurrentUser(data: User) {
    this.user = data;
    this.updateSupportServiceContext(true);
    this.triggerUpdateFromBackend();
    this.loadChatbotEnabled();
  }

  setPlanDetails(plan: SupportServicePlanDetails) {
    this.planDetails = plan;
    this.updateSupportServiceContext(false);
    this.triggerUpdateFromBackend();
  }

  abstract show(message?: string): void;

  abstract hide(): void;

  abstract resetSupportService(): void;

  abstract hideDefaultLauncher(): void;

  abstract showDefaultLauncher(): void;

  abstract updateSupportServiceContext(initial: boolean): void;

  isLiveChatOpen() {
    return this._liveChatIsOpen$.getValue();
  }

  reset() {
    this.user = null;
    this.planDetails = null;
    this.chatbotEnabled$.next(false);
    this.resetSupportService();
  }

  isChatHiddenForTeam() {
    const chatDisabledTeams = [
      {
        env: 'us',
        name: 'nextsphere.com',
        id: 53
      }
    ];

    return (
      chatDisabledTeams.findIndex(team => {
        return team.env === __DEPLOYMENT_ENV__ && team.id === this.user.teamId;
      }) >= 0
    );
  }

  triggerUpdateFromBackend() {
    this._rxRequestService
      .post(
        `${this._appConfig.getSecurityServiceURL()}/users/zendesk-and-intercom`,
        BACKGROUND_NETWORK_REQ_OPTIONS
      )
      .toPromise().then();
  }

  /** Fetches the AI chatbot enabled flag once (on login) and publishes it on
   *  {@link chatbotEnabled$}. Failures fall back to disabled. */
  loadChatbotEnabled() {
    this._rxRequestService
      .get(
        `${this._appConfig.getSecurityServiceURL()}/users/ai/zendesk/enabled`,
        BACKGROUND_NETWORK_REQ_OPTIONS
      )
      .toPromise()
      // RxRequestService already unwraps the response to the body, which is { enabled: boolean }.
      .then((res: any) => this.chatbotEnabled$.next(res?.enabled === true))
      .catch(() => this.chatbotEnabled$.next(false));
  }
}
